import React, { useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";

import EventCard from "@/components/events/EventCard";
import PageHeader from "@/components/PageHeader";
import Seo from "@/components/seo";
import styles from "@/styles/Events.module.css";
import {
  eventsOn,
  groupByMonth,
  normalizeEvent,
  upcomingFrom,
} from "src/lib/events";

const EventsCalendar = dynamic(
  () => import("@/components/events/EventsCalendar"),
  { ssr: false }
);

const INITIAL_VISIBLE = 8;

const REVALIDATE_OK = 900; // 15 minutes
const REVALIDATE_ERROR = 60; // retry sooner when Google is unreachable

const GOOGLE_CALENDAR_EMBED =
  "https://calendar.google.com/calendar/embed?src=c_fgvvfdkl7f1lmc7o41omquv6ps%40group.calendar.google.com";

export async function getStaticProps() {
  // Imported here rather than at module scope so the API key stays out of the
  // client bundle — Next only strips server-only code reached from this function.
  const { fetchCalendarEvents } = await import("src/lib/google-calendar");

  try {
    return {
      props: {
        events: await fetchCalendarEvents(),
        failed: false,
        generatedAt: new Date().toISOString(),
      },
      revalidate: REVALIDATE_OK,
    };
  } catch (error) {
    console.error("Failed to fetch events:", error);
    return {
      props: {
        events: [],
        failed: true,
        generatedAt: new Date().toISOString(),
      },
      revalidate: REVALIDATE_ERROR,
    };
  }
}

export default function Events({ events: rawEvents, failed, generatedAt }) {
  const events = useMemo(
    () => rawEvents.map(normalizeEvent).filter(Boolean),
    [rawEvents]
  );

  // null means "everything upcoming", which is the default view.
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);

  // The first render has to match on both sides, so it uses the server's clock;
  // the visitor's own "today" is only knowable after mount. Seeding from
  // `generatedAt` rather than null keeps the agenda in the server HTML — render
  // it empty and the page ships saying the calendar is empty.
  const [today, setToday] = useState(() => new Date(generatedAt));
  useEffect(() => setToday(new Date()), []);

  const upcoming = useMemo(
    () => upcomingFrom(events, today),
    [events, today]
  );
  const visibleUpcoming =
    showAllUpcoming || upcoming.length <= INITIAL_VISIBLE
      ? upcoming
      : upcoming.slice(0, INITIAL_VISIBLE);
  const remainingCount = upcoming.length - visibleUpcoming.length;
  const months = useMemo(
    () => groupByMonth(visibleUpcoming),
    [visibleUpcoming]
  );
  const dayEvents = selectedDate ? eventsOn(events, selectedDate) : [];

  const heading = selectedDate
    ? selectedDate.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "Upcoming";

  return (
    <div className={`container ${styles.page}`}>
      <Seo title="Events" />

      <header className={styles.header}>
        <PageHeader>Events</PageHeader>
        <p>
          Shows, meetings, and station happenings. Pick a date to see just that
          day, or browse everything coming up.
        </p>
      </header>

      <div className={styles.layout}>
        {/* CALENDAR — a filter for the agenda beside it */}
        <aside className={styles.filterColumn}>
          <div className={styles.calendarCard}>
            <EventsCalendar
              events={events}
              selectedDate={selectedDate}
              selectedDayClassName={styles.selectedDay}
              onDateClick={setSelectedDate}
              onEventClick={setSelectedDate}
            />
          </div>
          {selectedDate && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => {
                setSelectedDate(null);
                setShowAllUpcoming(false);
              }}
            >
              ← Show everything upcoming
            </button>
          )}
        </aside>

        {/* AGENDA — the primary content */}
        <section className={styles.agendaColumn}>
          <div className={styles.agendaHead}>
            <h2 className={styles.agendaTitle}>{heading}</h2>
            {!selectedDate && upcoming.length > 0 && (
              <span className={styles.agendaCount}>
                {upcoming.length} {upcoming.length === 1 ? "event" : "events"}
              </span>
            )}
          </div>

          <div aria-live="polite">
            {failed ? (
              <div className={styles.errorNote}>
                <p className={styles.errorTitle}>
                  We couldn&rsquo;t load the calendar.
                </p>
                <p className={styles.errorBody}>
                  This is on our end, not yours. Try again in a few minutes, or
                  see the{" "}
                  <a
                    href={GOOGLE_CALENDAR_EMBED}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    full KVRX calendar on Google
                  </a>
                  .
                </p>
              </div>
            ) : selectedDate ? (
              dayEvents.length > 0 ? (
                <ul className={styles.eventsList}>
                  {dayEvents.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </ul>
              ) : (
                <p className={styles.empty}>
                  Nothing scheduled for this date.{" "}
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => setSelectedDate(null)}
                  >
                    See what&rsquo;s coming up
                  </button>
                </p>
              )
            ) : months.length > 0 ? (
              <>
                {months.map((month) => (
                  <div key={month.key} className={styles.monthGroup}>
                    <h3 className={styles.monthLabel}>{month.label}</h3>
                    <ul className={styles.eventsList}>
                      {month.events.map((event) => (
                        <EventCard key={event.id} event={event} showDay />
                      ))}
                    </ul>
                  </div>
                ))}
                {remainingCount > 0 && (
                  <button
                    type="button"
                    className={styles.showAllButton}
                    onClick={() => setShowAllUpcoming(true)}
                  >
                    Show {remainingCount} more{" "}
                    {remainingCount === 1 ? "event" : "events"}
                  </button>
                )}
              </>
            ) : (
              <p className={styles.empty}>
                Nothing on the calendar right now. Check the{" "}
                <a
                  href={GOOGLE_CALENDAR_EMBED}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  KVRX calendar on Google
                </a>{" "}
                for the latest.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
